import { useState } from "react";
import { Download, Search, Trash2 } from "lucide-react";
import { adminApi, formatApiError } from "../../lib/adminApi";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const exportCsv = (rows) => {
  const table = [["Email", "City", "Joined (UTC)"], ...rows.map((e) => [e.email, e.city || "", new Date(e.joined_at).toISOString()])];
  const csv = table.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "community-hearts-waitlist.csv";
  a.click();
  URL.revokeObjectURL(url);
};

export const WaitlistTable = ({ filtered, cityStats, search, setSearch, cityFilter, setCityFilter, onDelete, deletingId }) => (
  <>
    <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative sm:w-80">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-cream/40" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search email or city" aria-label="Search waitlist" data-testid="admin-search-input" className="h-11 rounded-full border-cream/20 bg-cream/5 pl-11 text-cream placeholder:text-cream/40 focus-visible:ring-2 focus-visible:ring-gold" />
        </div>
        <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} aria-label="Filter by city" data-testid="admin-city-filter" className="h-11 cursor-pointer appearance-none rounded-full border border-cream/20 bg-cream/5 px-5 text-sm font-semibold text-cream outline-none focus:ring-2 focus:ring-gold">
          <option value="all" className="text-ink">All cities</option>
          {cityStats.map(([city]) => <option key={city} value={city} className="text-ink">{city}</option>)}
        </select>
      </div>
      <Button onClick={() => exportCsv(filtered)} data-testid="admin-export-csv-button" className="h-11 rounded-full bg-gold px-6 text-xs font-bold uppercase tracking-[0.15em] text-ink transition-colors duration-200 hover:bg-gold-deep active:scale-95">
        <Download className="mr-2 h-4 w-4" />
        Export CSV ({filtered.length})
      </Button>
    </div>

    <div className="mt-4 overflow-hidden rounded-3xl border border-cream/10" data-testid="admin-table">
      <div className="grid grid-cols-12 gap-4 border-b border-cream/10 bg-cream/5 px-6 py-4 text-[11px] font-bold uppercase tracking-[0.2em] text-cream/40">
        <span className="col-span-4">Email</span>
        <span className="col-span-3">City</span>
        <span className="col-span-1 text-center">Refs</span>
        <span className="col-span-3 text-right">Joined</span>
        <span className="col-span-1 text-right">Remove</span>
      </div>
      {filtered.length === 0 ? (
        <p className="px-6 py-10 text-center text-sm text-cream/40" data-testid="admin-empty-state">No signups match your search.</p>
      ) : (
        filtered.map((e, i) => (
          <div key={e.id} className="grid grid-cols-12 items-center gap-4 border-b border-cream/5 px-6 py-4 text-sm last:border-0" data-testid={`admin-row-${i}`}>
            <span className="col-span-4 truncate text-cream/90">{e.email}</span>
            <span className="col-span-3 truncate text-cream/60">{e.city || "—"}</span>
            <span className="col-span-1 text-center font-bold text-gold">{e.ref_count || 0}</span>
            <span className="col-span-3 text-right text-cream/60">
              {new Date(e.joined_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </span>
            <span className="col-span-1 text-right">
              <button onClick={() => onDelete(e.id)} disabled={deletingId === e.id} aria-label={`Remove ${e.email}`} data-testid={`admin-delete-${i}`} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-cream/15 text-cream/40 transition-colors duration-300 hover:border-red-400 hover:text-red-400 disabled:opacity-40">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </span>
          </div>
        ))
      )}
    </div>
  </>
);

const saveLabel = (state) => {
  if (state === "saving") return "Saving…";
  if (state === "saved") return "Saved";
  return "Save";
};

export const EmailSettingsCard = ({ initialHours, onSaved }) => {
  const [hours, setHours] = useState(String(initialHours));
  const [state, setState] = useState("idle");

  const save = async (e) => {
    e.preventDefault();
    setState("saving");
    try {
      const { data } = await adminApi.put("/admin/settings", { referral_nudge_hours: Number(hours) });
      setHours(String(data.referral_nudge_hours));
      setState("saved");
      setTimeout(() => setState("idle"), 2500);
      onSaved();
    } catch (err) {
      setState("error:" + formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <div className="mt-8 rounded-3xl border border-cream/10 p-6" data-testid="admin-settings">
      <p className="mb-1 text-xs font-bold uppercase tracking-[0.2em] text-cream/40">Email settings</p>
      <p className="text-sm text-cream/50">How long after joining should members get their referral-link reminder?</p>
      <form onSubmit={save} className="mt-4 flex flex-wrap items-center gap-3">
        <Input type="number" min={1} max={336} required value={hours} onChange={(e) => setHours(e.target.value)} aria-label="Referral nudge delay in hours" data-testid="admin-nudge-hours-input" className="h-11 w-28 rounded-full border-cream/20 bg-cream/5 px-5 text-cream focus-visible:ring-2 focus-visible:ring-gold" />
        <span className="text-sm text-cream/50">hours after signup</span>
        <Button type="submit" disabled={state === "saving"} data-testid="admin-settings-save" className="h-11 rounded-full bg-gold px-6 text-xs font-bold uppercase tracking-[0.15em] text-ink transition-colors duration-200 hover:bg-gold-deep active:scale-95">
          {saveLabel(state)}
        </Button>
        {state === "saved" && <span className="text-xs font-semibold text-gold" data-testid="admin-settings-saved">Applies to future nudges</span>}
        {state.startsWith("error:") && <span className="text-xs font-semibold text-red-400" data-testid="admin-settings-error">{state.slice(6)}</span>}
      </form>
    </div>
  );
};
